import { DYNAMIC_ATTRIBUTE_FORM_CONFIG } from '../constants/global';

/**
 * Parameters for checking if a form is a Dynamic Attribute form.
 */
interface DynamicAttributeFormParams {
  urlSlug?: string;
  customFormUid?: string;
}

/**
 * Checks if a form is a Dynamic Attribute (DA) form based on urlSlug or customFormUid.
 *
 * A form is identified as a DA form if either:
 * - The urlSlug starts with the DA prefix ('sav-da')
 * - The customFormUid starts with the DA prefix ('sav-da')
 *
 * @param params - Object containing urlSlug and/or customFormUid
 * @returns true if the form is a DA form, false otherwise
 *
 * @example
 * // Returns true
 * isDynamicAttributeForm({ urlSlug: 'sav-da-my-form' });
 *
 * @example
 * // Returns true
 * isDynamicAttributeForm({ customFormUid: 'sav-da-123' });
 *
 * @example
 * // Returns false
 * isDynamicAttributeForm({ urlSlug: 'my-normal-form' });
 */
export const isDynamicAttributeForm = ({
  urlSlug,
  customFormUid,
}: DynamicAttributeFormParams): boolean => {
  const prefix = DYNAMIC_ATTRIBUTE_FORM_CONFIG.PREFIX;

  const urlSlugMatch = urlSlug?.startsWith(prefix) ?? false;
  const customFormUidMatch = customFormUid?.startsWith(prefix) ?? false;

  return urlSlugMatch || customFormUidMatch;
};

/**
 * Returns the maximum columns value based on whether it's a DA form.
 *
 * @param isDaForm - Whether the form is a Dynamic Attribute form
 * @returns 3 for DA forms, 4 for normal forms
 *
 * @example
 * getDaFormMaxColumns(true);  // Returns 3
 * getDaFormMaxColumns(false); // Returns 4
 */
export const getDaFormMaxColumns = (isDaForm: boolean): number =>
  isDaForm ? DYNAMIC_ATTRIBUTE_FORM_CONFIG.MAX_COLUMNS : 4;

/**
 * Returns whether HTML labels should be enabled based on form type.
 *
 * @param isDaForm - Whether the form is a Dynamic Attribute form
 * @returns true if HTML labels should be enabled, false otherwise
 *
 * @example
 * shouldEnableHtmlLabels(true);  // Returns true (if ENABLE_HTML_LABELS is true)
 * shouldEnableHtmlLabels(false); // Returns false
 */
export const shouldEnableHtmlLabels = (isDaForm: boolean): boolean =>
  isDaForm && DYNAMIC_ATTRIBUTE_FORM_CONFIG.ENABLE_HTML_LABELS;
